/* Small controlled-input building blocks shared by every domain's capture
   form fields (designs/intake-multi-domain.md MD4) — split out of
   CaptureForm.tsx so PerceptionDetailFields/GncDetailFields (and future
   domains') can reuse them without duplicating the same select/checklist
   markup per domain. */
import { useId, type JSX } from "react";
import type { FieldHelp } from "./form-help";
import type { VocabOption } from "../../../shared/capabilities";

export function Select({
  label,
  value,
  options,
  onChange,
  placeholder,
  help,
  optionTitle,
}: {
  label: string;
  value: string;
  options: readonly VocabOption[];
  onChange: (value: string) => void;
  /* the field's hint + hover (intake-form-help.md FH4), and a hover per
     choice (FH5); both optional */
  help?: FieldHelp;
  optionTitle?: (value: string) => string | undefined;
  /* an OPTIONAL field's empty choice. Present only where leaving the answer
     out is itself honest — an unanswered question reads "not recorded", and
     the empty value is "" so the caller can map it to null. */
  placeholder?: string;
}): JSX.Element {
  const hintId = useId();
  return (
    <div className="capture-field">
      <label>
        <span title={help?.more}>{label}</span>
        <select
          value={value}
          aria-describedby={help === undefined ? undefined : hintId}
          onChange={(e) => onChange(e.target.value)}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value} title={optionTitle?.(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      {help !== undefined && (
        <small className="capture-hint" id={hintId}>
          {help.hint}
        </small>
      )}
    </div>
  );
}

export function Checks({
  label,
  options,
  picked,
  onToggle,
}: {
  label: string;
  options: readonly VocabOption[];
  picked: string[];
  onToggle: (value: string) => void;
}): JSX.Element {
  return (
    <div className="capture-field">
      <span>{label}</span>
      <div className="capture-checks">
        {options.map((o) => (
          <label key={o.value} className={picked.includes(o.value) ? "capture-check on" : "capture-check"}>
            <input
              type="checkbox"
              checked={picked.includes(o.value)}
              onChange={() => onToggle(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

export const toggle = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
